import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Alert, Button, Card } from 'antd'
import { confirmEmailLink } from '@/features/auth/api'
import { setSession } from '@/lib/session'
import { buildAuthSession } from '../session'
import { resolvePostLoginTarget } from '../returnTo'
import { interpretEmailLinkError } from '../authFlow'
import AuthLogo from '../components/AuthLogo'
import { clearExtensionIntent, postAuthSuccessToExtension, restoreExtensionIntent } from '../extensionAuth'
import { authLog } from '../debug'

export default function ConfirmLoginPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<{ message: string; canRetry: boolean } | null>(
    id ? null : { message: 'This sign-in link is invalid.', canRetry: false }
  )

  // Only on click: the link is single use, and email security scanners open links (but don't
  // press buttons), so redeeming it on page load would let a scanner use it up.
  const onContinue = async () => {
    if (!id || loading) return
    setError(null)
    setLoading(true)
    try {
      const res = await confirmEmailLink(id)
      if (!res?.idToken || !res.refreshToken || !res.email) {
        setError({ message: 'This sign-in link has expired or was already used. Please sign in again.', canRetry: false })
        return
      }

      const nextSession = await buildAuthSession(res.email, res.idToken, res.refreshToken, res.expiry ?? 3600)
      setSession(nextSession)
      const extensionIntent = restoreExtensionIntent()
      if (extensionIntent.isExtension) {
        postAuthSuccessToExtension({
          idToken: nextSession.idToken,
          refreshToken: nextSession.refreshToken,
          tokenExpiry: nextSession.tokenExpiry,
        })
        clearExtensionIntent()
        if (extensionIntent.redirectUri) {
          window.location.href = extensionIntent.redirectUri
          return
        }
      }
      // The same resolver as the other login paths: it consumes the saved
      // target and loads the permissions its route guard reads.
      navigate(await resolvePostLoginTarget(nextSession), { replace: true })
    } catch (e) {
      authLog('Confirm login link failed', e)
      setError(interpretEmailLinkError(e))
    } finally {
      setLoading(false)
    }
  }

  const finished = error && !error.canRetry

  return (
    <div className="min-h-screen bg-[#d1e8e8] flex flex-col items-center justify-center p-6">
      <AuthLogo className="h-14 w-auto mb-6" />
      <Card className="w-full max-w-md">
        <h1 className="text-xl font-semibold text-gray-900 mb-2">{finished ? 'Unable to sign in' : 'Finish signing in'}</h1>
        <div aria-live="polite">{error && <Alert type="error" showIcon title={error.message} className="mb-4" />}</div>
        {finished ? (
          <Link to="/login">
            <Button type="primary">Go to sign in</Button>
          </Link>
        ) : (
          <>
            <p className="text-gray-600 mb-4">Continue to sign in to Dashboard Insights on this browser.</p>
            <Button type="primary" loading={loading} onClick={() => void onContinue()} autoFocus>
              Continue sign-in
            </Button>
          </>
        )}
      </Card>
    </div>
  )
}
